import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { AlertTriangle, X, Ban, UserX, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CANCEL_REASONS = [
  { value: 'CLIENT', label: 'Annulation client', description: 'Le client a annulé sa réservation' },
  { value: 'OTA', label: 'Annulation OTA', description: 'Annulation par la plateforme (Booking, Airbnb...)' },
  { value: 'NO_SHOW', label: 'No-show', description: 'Le client ne s\'est pas présenté' },
  { value: 'BOOKING_ERROR', label: 'Erreur de réservation', description: 'Double réservation, mauvaises dates...' },
  { value: 'OTHER', label: 'Autre', description: 'Autre motif' }
];

export default function CancelReservationDialog({ 
  reservation, 
  open, 
  onOpenChange, 
  onCancelled 
}) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  
  const hasPayments = reservation?.payments?.length > 0;
  const totalPaid = reservation?.payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
  
  const handleCancel = async () => {
    if (!reason) {
      toast.error('Veuillez sélectionner un motif d\'annulation');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          cancel_reason: reason,
          cancel_notes: notes || null
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erreur lors de l\'annulation');
      }
      
      const data = await response.json();
      toast.success('Réservation annulée');
      onCancelled?.(data.reservation);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setReason('');
    setNotes('');
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Ban className="w-5 h-5" />
            Annuler la réservation
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Warning about payments */}
          {hasPayments && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Paiements enregistrés</p>
                <p className="text-sm text-amber-700">
                  Cette réservation contient {formatCurrency(totalPaid)} de paiements.
                  L'annulation ne supprime pas les paiements. Un remboursement devra être traité manuellement.
                </p>
              </div>
            </div>
          )}
          
          {/* Reason selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Motif d'annulation *</Label>
            <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
              {CANCEL_REASONS.map((r) => (
                <div key={r.value} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value={r.value} id={r.value} className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor={r.value} className="font-medium cursor-pointer">
                      {r.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{r.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
          
          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Notes (optionnel)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires..."
              rows={2}
            />
          </div>
          
          {/* Confirmation message */}
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Attention :</strong> Cette action est définitive. La réservation sera marquée comme annulée
              et les nuits seront libérées dans le calendrier.
            </p>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            <X className="w-4 h-4 mr-2" />
            Fermer
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleCancel} 
            disabled={loading || !reason}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Ban className="w-4 h-4 mr-2" />
            )}
            Confirmer l'annulation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// No-Show Dialog
export function NoShowDialog({ 
  reservation, 
  open, 
  onOpenChange, 
  onMarked 
}) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  
  const handleMarkNoShow = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/no-show`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ notes: notes || null })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erreur');
      }
      
      const data = await response.json();
      toast.success('Réservation marquée comme No-Show');
      onMarked?.(data.reservation);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <UserX className="w-5 h-5" />
            Marquer comme No-Show
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Le client ne s'est pas présenté pour sa réservation du{' '}
            <strong>{new Date(reservation?.check_in).toLocaleDateString('fr-FR')}</strong>.
          </p>
          
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              <strong>Note :</strong> Contrairement à une annulation, les nuits resteront bloquées 
              dans le calendrier (pas de revente possible).
            </p>
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-2 block">Notes (optionnel)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Raison du no-show, tentatives de contact..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button 
            variant="default"
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleMarkNoShow} 
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserX className="w-4 h-4 mr-2" />
            )}
            Confirmer No-Show
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
